import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Quill from 'quill';

@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="rich-text-editor-wrapper">
      <!-- Quill Editor Container -->
      <div #editorContainer class="quill-container">
        <!-- Toolbar will be inserted here by Quill -->
        <div #toolbar id="toolbar-{{editorId}}" class="ql-toolbar ql-snow">
          <button class="ql-bold" type="button"></button>
          <button class="ql-italic" type="button"></button>
          <button class="ql-underline" type="button"></button>
          <button class="ql-list" value="ordered" type="button"></button>
          <button class="ql-list" value="bullet" type="button"></button>
          <button class="ql-link" type="button"></button>
          <button class="ql-clean" type="button"></button>
        </div>
        <!-- Editor will be inserted here by Quill -->
        <div #editor id="editor-{{editorId}}" class="ql-editor-element"></div>
      </div>
      
      <!-- Character count (optional) -->
      <div *ngIf="showCharCount" class="text-xs text-gray-500 mt-2 text-right">
        {{ characterCount() }} characters
      </div>
    </div>
  `,
  styles: [`
    .rich-text-editor-wrapper {
      @apply w-full;
    }

    .quill-container {
      @apply border border-gray-200 rounded-lg bg-white;
    }

    /* Override Quill's default toolbar styling */
    :host ::ng-deep .ql-toolbar.ql-snow {
      @apply border-0 border-b border-gray-200 bg-gray-50 rounded-t-lg p-2;
    }

    :host ::ng-deep .ql-container.ql-snow {
      @apply border-0;
    }

    /* Style the editor area */
    :host ::ng-deep .ql-editor {
      @apply min-h-[200px] max-h-[400px] p-4 text-gray-900;
      font-size: 15px;
      line-height: 1.7;
    }

    :host ::ng-deep .ql-editor.ql-blank::before {
      @apply text-gray-400 italic;
    }

    /* Format styles */
    :host ::ng-deep .ql-editor p {
      @apply mb-3;
    }

    :host ::ng-deep .ql-editor strong {
      @apply font-bold;
    }

    :host ::ng-deep .ql-editor em {
      @apply italic;
    }

    :host ::ng-deep .ql-editor u {
      @apply underline;
    }

    :host ::ng-deep .ql-editor a {
      @apply text-blue-600 underline hover:text-blue-800 transition-colors;
    }

    /* List styles */
    :host ::ng-deep .ql-editor ol,
    :host ::ng-deep .ql-editor ul {
      @apply pl-8 mb-3;
    }

    :host ::ng-deep .ql-editor ol {
      @apply list-decimal;
    }

    :host ::ng-deep .ql-editor ul {
      @apply list-disc;
    }

    :host ::ng-deep .ql-editor li {
      @apply mb-1;
    }

    /* Disabled state */
    :host ::ng-deep .ql-container.ql-disabled .ql-editor {
      @apply opacity-50 cursor-not-allowed;
    }

    /* Focus state */
    .quill-container:focus-within {
      @apply ring-2 ring-blue-500 border-transparent;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ]
})
export class RichTextEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('editor', { static: true }) editorElement!: ElementRef;
  @ViewChild('toolbar', { static: true }) toolbarElement!: ElementRef;
  
  @Input() placeholder: string = 'Start typing...';
  @Input() showCharCount: boolean = false;
  @Output() contentChange = new EventEmitter<string>();
  
  private quill: Quill | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  
  disabled = signal(false);
  characterCount = signal(0);
  editorId = `editor-${Math.random().toString(36).substr(2, 9)}`;

  ngOnInit() {
    this.initializeEditor();
  }

  ngOnDestroy() {
    if (this.quill) {
      this.quill = null;
    }
  }

  private initializeEditor() {
    this.quill = new Quill(this.editorElement.nativeElement, {
      modules: {
        toolbar: this.toolbarElement.nativeElement
      },
      theme: 'snow',
      placeholder: this.placeholder,
      formats: ['bold', 'italic', 'underline', 'list', 'link']
    });

    // Listen for text changes
    this.quill.on('text-change', () => {
      const html = this.quill!.root.innerHTML;
      const text = this.quill!.getText();
      
      this.characterCount.set(text.trim().length);
      this.onChange(html);
      this.contentChange.emit(html);
    });

    // Listen for blur events
    this.quill.root.addEventListener('blur', () => {
      this.onTouched();
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (this.quill) {
      if (value) {
        this.quill.root.innerHTML = value;
      } else {
        this.quill.setText('');
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (this.quill) {
      this.quill.enable(!isDisabled);
    }
  }
}
